import { useEffect, useRef, useState } from 'react'
import styles from './InteractiveOverlayCard.module.scss'
import { Button, Heading } from '~elements'
import { Col } from 'react-grid-system'
import { AssetType, ButtonProps } from '~types'
import Image from 'next/image'
import ReactDOM from 'react-dom'

import { useWindowSize } from 'utils/hooks'
import { getCustomBreakpoint } from 'utils/hooks'
import { useIsIntersecting } from 'utils/hooks/useIsIntersecting'
import { useRouter } from 'next/router'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { getImageUrl } from 'utils/assets/getImageUrl'

interface SliderInterface {
  slickNext: () => void
  slickPrev: () => void
}
interface InteractiveOverlayCardProps {
  headline: string
  highlightedWords?: string
  subheadline: string
  eyebrow: string
  cta: ButtonProps
  listItems: string[]
  onListItemClick?: (index: number) => void
  backgroundImages?: AssetType[]
  mobileBackgroundImages?: AssetType[]
}

const InteractiveOverlayCard = ({
  headline,
  highlightedWords,
  eyebrow,
  subheadline,
  listItems,
  onListItemClick,
  backgroundImages,
  mobileBackgroundImages,
  cta
}: InteractiveOverlayCardProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { isBreakpoint: isSmartphoneScreen } = getCustomBreakpoint(431, '<')
  const { isBreakpoint: isBelowBreakpointMd } = useWindowSize('md', '<')
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)
  const [activeBackgroundImage, setActiveBackgroundImage] = useState<AssetType | null>(null)
  const sliderRef = useRef<SliderInterface | null>(null)
  const timerRef = useRef<number | null>(null)
  const [isMouseOver, setIsMouseOver] = useState(false)
  const [touchX, setTouchX] = useState<number | null>(null)
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null)
  const { query } = useRouter()
  const slug = query.slug
  const linkModule = 'interactive_overlay_card'
  // mocking slug for storybook purposes
  const source = Array.isArray(slug) ? slug[0] : slug ?? ''
  const portalRoot = typeof document !== 'undefined' && document.getElementById('portal-root')
  const swipeDistanceLimit = 50
  const isElementIntersecting = useIsIntersecting({
    ref: containerRef
  })

  useEffect(() => {
    if (isSmartphoneScreen) {
      if (mobileBackgroundImages && mobileBackgroundImages.length > 0) {
        setActiveBackgroundImage(mobileBackgroundImages[activeTabIndex] || '')
      }
    } else {
      if (backgroundImages && backgroundImages.length > 0) {
        setActiveBackgroundImage(backgroundImages[activeTabIndex] || '')
      }
    }
  }, [activeTabIndex, backgroundImages, mobileBackgroundImages, isSmartphoneScreen])

  // useEffect(() => {
  //   if (containerRef && containerRef.current) {
  //     //handleIsInView(isElementIntersecting)
  //   }
  // }, [isElementIntersecting, containerRef])
  const backgroundImageUrl = getImageUrl({ imageUrl: activeBackgroundImage?.url })

  const prevSlideHandler = () => {
    const newIndex = activeTabIndex === 0 ? listItems.length - 1 : activeTabIndex - 1
    setActiveTabIndex(newIndex)
    sliderRef?.current?.slickPrev()
  }

  const nextSlideHandler = () => {
    const newIndex = activeTabIndex === listItems.length - 1 ? 0 : activeTabIndex + 1
    setActiveTabIndex(newIndex)
    sliderRef?.current?.slickNext()
  }

  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0].isIntersecting
      if (isIntersecting && !isMouseOver) {
        timerRef.current = setInterval(() => {
          setActiveTabIndex(prevIndex => (prevIndex + 1) % listItems.length)
        }, 3000)
      } else {
        clearInterval(timerRef.current)
      }
    }

    const intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    })

    if (containerRef.current) {
      intersectionObserver.observe(containerRef.current)
    }

    return () => {
      clearInterval(timerRef.current)
      intersectionObserver.disconnect()
    }
  }, [activeTabIndex, listItems.length, isMouseOver])

  const handleListItemClick = (index: number) => {
    setActiveTabIndex(index)
    clearInterval(timerRef.current)
    onListItemClick && onListItemClick(index)
  }

  const handleBulletPointHover = () => {
    setIsMouseOver(true)
    clearInterval(timerRef.current)
  }

  const handleBulletPointLeave = () => {
    setIsMouseOver(false)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchX(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchX && listItems.length > 1) {
      const xPosition = e.touches[0].clientX
      const distance = Math.abs(touchX - xPosition)
      if (distance > swipeDistanceLimit) {
        if (touchX > xPosition) {
          setSwipeDirection('left')
        } else {
          setSwipeDirection('right')
        }
      }
    }
  }

  const handleTouchEnd = () => {
    if (touchX && listItems.length > 1) {
      if (swipeDirection === 'right') {
        prevSlideHandler()
      } else if (swipeDirection === 'left') {
        nextSlideHandler()
      }
    }
    setTouchX(null)
    setSwipeDirection(null)
  }

  const ctaButton = (
    <Button
      {...cta}
      {...(isBelowBreakpointMd &&
        isElementIntersecting && {
          size: 'xl',
          isFullWidth: true
        })}
      analytics={{ linkModule: linkModule }}
    />
  )

  return (
    <Col>
      <div
        className={styles['interactive-overlay-card-external']}
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {isBelowBreakpointMd && (
          <div className={styles['indicator-position']}>
            {listItems.map((listItem, index: number) => (
              <span
                key={`indicator-position-${index}`}
                className={`${styles['indicator']} ${index === activeTabIndex ? styles['active-indicator'] : ''}`}
              ></span>
            ))}
          </div>
        )}
        <div className={styles['overlay-card-container']}>
          <div className={styles['title-text']}>
            {headline && (
              <Heading level={2}>
                {headline}
                {highlightedWords && <span className={styles['highlight-word']}>{' ' + highlightedWords}</span>}
              </Heading>
            )}
            {eyebrow && (
              <Heading level={3} levelDisplay='eyebrow' color='light'>
                {eyebrow}
              </Heading>
            )}

            {isElementIntersecting && isBelowBreakpointMd && portalRoot
              ? // Using createPortal because
                // `backdrop-filter` property on parent makes fixed elements behave as absolute
                ReactDOM.createPortal(
                  <div
                    className='portal-fixed-cta-container'
                    // to be placed above fixed position CTA from EDPFullScreenVideo
                    style={{ zIndex: 99999 }}
                  >
                    {ctaButton}
                  </div>,
                  portalRoot
                )
              : ctaButton}
          </div>
          <div className={styles['offerings']}>
            {subheadline && (
              <Heading level={3} color='light'>
                {subheadline}
              </Heading>
            )}
            {listItems && (
              <div className={styles['list-items']}>
                <ul>
                  {listItems.map((listItem, index) => (
                    <li
                      className={
                        isSmartphoneScreen
                          ? activeTabIndex === index
                            ? styles['mobile-list-item']
                            : styles['backgroundTransparent']
                          : activeTabIndex === index
                          ? styles['backgroundBlack']
                          : styles['backgroundTransparent']
                      }
                      key={index}
                      onClick={() => handleListItemClick(index)}
                      onMouseEnter={handleBulletPointHover}
                      onMouseLeave={handleBulletPointLeave}
                    >
                      {listItem}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        {backgroundImageUrl && (
          <Image
            src={backgroundImageUrl}
            alt={activeBackgroundImage?.altText || ''}
            fill
            style={{ objectFit: 'cover' }}
            quality={NEXT_IMAGE_QUALITY}
          />
        )}
      </div>
    </Col>
  )
}

export default InteractiveOverlayCard
